// Aba "Tendências & Causa-Raiz" — análises da operação como um todo (não de um
// time específico): evolução temporal da reincidência + fluxo de causa-raiz.
import { memo, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  PriorityHighOutlined,
  ReplayCircleFilledOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { ChartCard, MetricCard, SemaforoLegend, formatInt } from "./shared";
import { GUIDES, MetricInfo } from "./metricInfo";
import {
  CartaControle,
  CronicosDistribuicao,
  CronicosTabela,
  CurvaReincidencia,
  Heatmap,
  HeatmapEscala,
  MatrizTaxa,
  SankeyMotivos,
} from "./simCharts";

// memo: a página re-renderiza a cada clique nos filtros; a aba só re-renderiza quando o dado/loading dela mudam.
export const TendenciasCausaRaizTab = memo(function TendenciasCausaRaizTab({
  styles,
  theme,
  curvaDiaria = [],
  spcSemanal = [],
  // fluxoMotivos, // desativado (Abertura → Fechamento)
  heatmapCidadeMotivo,
  matrizTipoMotivo,
  fluxoAnterior,
  cronicos,
  loading = false,
  carregando = {},
}) {
  const distribuicao = cronicos?.distribuicao || [];
  const top = cronicos?.top || [];
  const cronicosCriticos = distribuicao
    .filter((d) => ["3x", "4x", "5x+"].includes(d.faixa))
    .reduce((s, d) => s + (d.clientes || 0), 0);

  // Visualização da causa-raiz: "fluxo" (padrão) ou "matriz".
  const [visCausa, setVisCausa] = useState("fluxo");

  const dia7 = curvaDiaria.find((d) => d.dia === 7);
  const dia90 = curvaDiaria.find((d) => d.dia === 90);
  const semanasForaControle = spcSemanal.filter((s) => s.foraControle).length;

  const temCurva = curvaDiaria.length > 0;
  const temSpc = spcSemanal.length > 0;
  // const temSankey = (fluxoMotivos?.links?.length || 0) > 0; // desativado (Abertura → Fechamento)
  const temHeatmap = (heatmapCidadeMotivo?.matrix?.length || 0) > 0;
  const temMatriz = (matrizTipoMotivo?.tipos?.length || 0) > 0;
  const temFluxoAnterior = (fluxoAnterior?.links?.length || 0) > 0;
  const temCronicos = top.length > 0;

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 2, sm: 4, md: 6, lg: 10, xl: 10 }}
      sx={{ maxWidth: "100%", minWidth: 0 }}
    >
      {/* KPIs */}
      <Grid item xs={2} sm={2} md={3} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={loading && !temCurva}
          atualizando={carregando.curva}
          icon={ReplayCircleFilledOutlined}
          faixa={dia7 ? "atencao" : null}
          title="Reincidência na 1ª semana"
          value={dia7 ? `${dia7.acumulado}%` : "-"}
          helper={dia90 ? `Acumulado até o 7º dia (de ${dia90.acumulado}% em 90d)` : "Acumulado até o 7º dia"}
        />
      </Grid>
      <Grid item xs={2} sm={2} md={3} lg={3}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={loading && !temSpc}
          atualizando={carregando.spc}
          icon={WarningAmberOutlined}
          faixa={temSpc ? (semanasForaControle > 0 ? "critico" : "ok") : null}
          title="Semanas fora de controle"
          value={formatInt(semanasForaControle)}
          helper="Acima do limite estatístico (UCL)"
        />
      </Grid>
      <Grid item xs={2} sm={2} md={3} lg={3}>
        <MetricCard
          styles={styles}
          theme={theme}
          loading={loading && !temCronicos}
          atualizando={carregando.cronicos}
          icon={PriorityHighOutlined}
          faixa={temCronicos ? (cronicosCriticos > 0 ? "atencao" : "ok") : null}
          title="Instalações crônicas (3x+)"
          value={formatInt(cronicosCriticos)}
          helper="Provável causa de infraestrutura"
        />
      </Grid>

      {/* Tendências */}
      <Grid item xs={2} sm={4} md={6} lg={6}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !temCurva}
          atualizando={carregando.curva}
          title="Curva de reincidência por dia"
          subtitle="Quando o problema volta após o fechamento"
          topRight={<MetricInfo theme={theme} guide={GUIDES.curva} />}
          noData={!temCurva}
          noDataMessage="Não foram encontradas reincidências para montar a curva."
        >
          <CurvaReincidencia styles={styles} theme={theme} data={curvaDiaria} />
        </ChartCard>
      </Grid>
      <Grid item xs={2} sm={4} md={6} lg={4}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !temSpc}
          atualizando={carregando.spc}
          title="Carta de controle (SPC)"
          subtitle="Reincidência semanal vs limites estatísticos"
          topRight={<MetricInfo theme={theme} guide={GUIDES.spc} />}
          noData={!temSpc}
          noDataMessage="Não foram encontradas semanas suficientes para a carta de controle."
        >
          <CartaControle styles={styles} theme={theme} data={spcSemanal} />
        </ChartCard>
      </Grid>

      {/* DESATIVADO (Abertura → Fechamento): para voltar, descomente este card, o import e a prop fluxoMotivos aqui, o estado/fetch em index.js e a rota no backend.
      <Grid item xs={2} sm={4} md={6} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !temSankey}
          atualizando={loading}
          title="Abertura → Fechamento"
          subtitle="Fluxo de causa-raiz dos chamados"
          topRight={<MetricInfo theme={theme} guide={GUIDES.sankey} />}
          noData={!temSankey}
          noDataMessage="Não foram encontrados motivos de fechamento suficientes para o fluxo."
        >
          <SankeyMotivos styles={styles} theme={theme} data={fluxoMotivos} />
        </ChartCard>
      </Grid>
      */}

      {/* Causa-raiz: fluxo (padrão) ou matriz, alternáveis */}
      <Grid item xs={2} sm={4} md={6} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !(visCausa === "fluxo" ? temFluxoAnterior : temMatriz)}
          atualizando={visCausa === "fluxo" ? carregando.fluxoAnterior : carregando.matriz}
          title="Causa-raiz da reincidência"
          subtitle={
            visCausa === "fluxo"
              ? "Só reincidentes (90 dias): como a OS anterior foi fechada → qual tipo de OS o cliente abriu depois"
              : "% das OS de cada tipo × motivo de fechamento que tiveram nova OS do mesmo cliente em até 90 dias (atenuado: menos de 20 OS)"
          }
          topRight={
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
              {visCausa === "matriz" && <SemaforoLegend theme={theme} />}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={visCausa}
                onChange={(_e, v) => v && setVisCausa(v)}
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12.5,
                    px: 1.5,
                    borderColor: alpha(theme.palette.text.primary, 0.16),
                  },
                }}
              >
                <ToggleButton value="fluxo">Fluxo</ToggleButton>
                <ToggleButton value="matriz">Matriz</ToggleButton>
              </ToggleButtonGroup>
              <MetricInfo
                theme={theme}
                guide={visCausa === "fluxo" ? GUIDES.causaRaizFluxo : GUIDES.causaRaizMatriz}
              />
            </Stack>
          }
          noData={visCausa === "fluxo" ? !temFluxoAnterior : !temMatriz}
          noDataMessage={
            visCausa === "fluxo"
              ? "Não foram encontradas reincidências no período."
              : "Não foram encontradas OS com motivo de fechamento no período."
          }
        >
          {visCausa === "fluxo" ? (
            <SankeyMotivos
              styles={styles}
              theme={theme}
              data={fluxoAnterior}
              nodePadding={12}
              unidade="retornos"
              altura={420}
              rotulosFora
            />
          ) : (
            <MatrizTaxa theme={theme} data={matrizTipoMotivo} />
          )}
        </ChartCard>
      </Grid>
      <Grid item xs={2} sm={4} md={6} lg={10}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !temHeatmap}
          atualizando={carregando.heatmap}
          title="Cidade × Motivo"
          subtitle="Concentração de chamados por motivo"
          topRight={<HeatmapEscala theme={theme} />}
          noData={!temHeatmap}
          noDataMessage="Não foram encontradas cidades suficientes para o mapa de calor."
        >
          <Heatmap theme={theme} data={heatmapCidadeMotivo} />
        </ChartCard>
      </Grid>

      {/* Crônicos */}
      <Grid item xs={2} sm={4} md={6} lg={6}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !temCronicos}
          atualizando={carregando.cronicos}
          title="Distribuição de instalações crônicas"
          subtitle="Quantas vezes a mesma instalação reincidiu no período"
          noData={!temCronicos}
          noDataMessage="Não foram encontradas instalações com reincidência no período."
        >
          <CronicosDistribuicao styles={styles} theme={theme} data={distribuicao} />
        </ChartCard>
      </Grid>
      <Grid item xs={2} sm={4} md={6} lg={4}>
        <ChartCard
          styles={styles}
          theme={theme}
          loading={loading && !temCronicos}
          atualizando={carregando.cronicos}
          title="Top instalações crônicas"
          subtitle="Maior número de retornos no período"
          noData={!temCronicos}
          noDataMessage="Não foram encontradas instalações crônicas no período."
        >
          <CronicosTabela styles={styles} theme={theme} data={top} />
        </ChartCard>
      </Grid>
    </Grid>
  );
});
